import { faTrash } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import Client from "fhir-kit-client";
import { Bundle, BundleEntry, CareTeam, FhirResource, Organization, Patient, Practitioner, PractitionerRole, RelatedPerson } from "fhir/r4";
import i18n from "i18next";
import { useTranslation } from "react-i18next";
import { FunctionComponent, useCallback, useEffect, useReducer, useRef, useState } from "react";
import { Alert, Button, Card, Dropdown, Form, InputGroup } from "react-bootstrap";
import { useNavigate, useParams } from "react-router-dom";
import AcesoPage from "src/components/AcesoPage/AcesoPage";
import NoteList from "src/components/NoteList/NoteList";
import OrganizationForm from "src/components/OrganizationForm/OrganizationForm";
import PractitionerForm from "src/components/PractitionerForm/PractitionerForm";
import RelatedPersonForm from "src/components/RelatedPersonForm/RelatedPersonForm";
import NavTabs from "src/components/navTabs/NavTabs";
import { Title } from "@fyrstain/fhir-front-library";
import { toPublicUrl } from "src/services/PublicUrl";
import DataTable, { DataTableColumn } from "src/components/DataTable/DataTable";
import styles from './patientsViewer.module.css';

interface Participant {
    id: string,
    type: 'Practitioner' | 'RelatedPerson' | 'Organization' | 'PractitionerRole',
    create: boolean,
    value: FhirResource | BundleEntry<FhirResource>[] | undefined,
    start: string | undefined,
    end: string | undefined,
    dependsOn?: string[];
};

interface CareCircleRow {
    id: string;
    participants: string;
    startperiod: string;
    endperiod: string;
}

const PatientsViewer: FunctionComponent = () => {
    const { t } = useTranslation();

    /////////////////////////////////////
    //             State               //
    //////////////////////////////////

    const [loading, setLoading] = useState(false);

    const { patientId } = useParams();

    const [lastname, setLastname] = useState("");
    const [firstname, setFirstname] = useState("");
    const [birthdate, setBirthdate] = useState("");
    const [gender, setGender] = useState("");

    const [createdNow, setCreatedNow] = useState(false);
    const [careTeam, setCareTeam] = useState(undefined as CareTeam | undefined);

    const [careTeamStartPeriod, setCareTeamStartPeriod] = useState("");
    const [careTeamEndPeriod, setCareTeamEndPeriod] = useState("");

    const [counter, forceUpdate] = useReducer(x => x + 1, 0);

    const [participantSubmitTrigger, triggerSubmit] = useState(false);
    const [participantResetTrigger, triggerReset] = useState(false);

    const [participantStart, setParticipantStart] = useState(undefined as string | undefined);
    const [participantId, setParticipantId] = useState('');
    const [createParticipant, setCreateParticipant] = useState(false);

    const [practitionerChoices, setPractitionerChoices] = useState([] as Practitioner[]);
    const [relatedPersonChoices, setRelatedPersonChoices] = useState([] as RelatedPerson[]);
    const [organizationChoices, setOrganizationChoices] = useState([] as Organization[]);

    const [practionnerToggle, setPractionnerToggle] = useState(false);
    const [relatedPersonToggle, setRelatedPersonToggle] = useState(false);
    const [organizationToggle, setOrganizationToggle] = useState(false);

    const [participants, setParticipants] = useState([] as Participant[]);

    const [careCircleData, setCareCircleData] = useState<CareCircleRow[]>([]);
    const [careCirclecolumns] = useState<DataTableColumn<CareCircleRow>[]>([
        { text: 'Participants', dataField: "participants" },
        { text: i18n.t('label.startperiod'), dataField: "startperiod" },
        { text: i18n.t('label.endperiod'), dataField: "endperiod" },
        {
            text: i18n.t('Actions'),
            dataField: "id",
            formatter: (cell: string) => {
                return <FontAwesomeIcon
                    icon={faTrash}
                    className="actionIcon"
                    onClick={() => {
                        setCareCircleData(careCircleData.filter(p => p.id !== cell));
                        setParticipants(participants.filter(p => p.id !== cell));
                    }}
                />
            },
        }
    ]);

    /////////////////////////////////////
    //             Client             //
    ////////////////////////////////////

    const fhirUrl =
        localStorage.getItem("serverAddress") ||
        process.env.REACT_APP_FHIR_URL;

    if (!fhirUrl) {
        throw new Error("Aucune URL de serveur FHIR n'est configurée");
    }

    const fhirClient = new Client({
        baseUrl: process.env.REACT_APP_FHIR_URL ?? "fhir",
    });

    /////////////////////////////////////
    //             Actions               //
    //////////////////////////////////

    const navigate = useNavigate();

    const onError = useCallback((error?: unknown) => {
        navigate("/Error", { state: { error } });
    }, [navigate]);

    const reloadPage = useCallback(() => {
        navigate("/PatientsViewer/" + patientId);
    }, [navigate, patientId]);

    /**
     * Load the selected patient to populate the fields.
     */
    const loadPatient = async (): Promise<void> => {
        if (!patientId) {
            return;
        }

        try {
            const patient = await fhirClient.read({
                resourceType: "Patient",
                id: patientId,
            }) as Patient;

            const patientName = patient.name?.[0];

            setFirstname(patientName?.given?.join(" ") ?? "");
            setLastname(patientName?.family ?? "");
            setBirthdate(patient.birthDate ?? "");
            setGender(patient.gender ?? "");
        } catch (error) {
            console.error("Erreur lors du chargement du patient :", error);
            onError();
        }
    };

    async function loadCareTeam() {
        try {
            const response = await fhirClient.search({
                resourceType: "CareTeam",
                searchParams: {
                    "_include": "CareTeam:*",
                    "patient": patientId ?? '',
                },
            }) as Bundle;

            const careTeamEntry = response.entry?.find(entry => 'CareTeam' === entry.resource?.resourceType);
            if (careTeamEntry) {
                const retrievedCareTeam = careTeamEntry.resource as CareTeam;
                setCareTeam(retrievedCareTeam);
                setCareTeamStartPeriod(retrievedCareTeam.period?.start ?? '');
                setCareTeamEndPeriod(retrievedCareTeam.period?.end ?? '');
                //Add participants
                retrievedCareTeam.participant?.forEach(participant => {
                    const participantEntry = response.entry?.find(entry => participant.member?.reference?.split('/')[1] === entry.resource?.id);
                    if (participantEntry && participantEntry.resource) {
                        switch (participantEntry.resource.resourceType) {
                            case 'Practitioner':
                                addPractitionerToList(participantEntry.resource.id ?? '',
                                    participantEntry.resource.name?.map(name => name.given?.join(',') + ' ' + name.family).find(() => true) ?? '',
                                    undefined,
                                    participant.period?.start,
                                    participant.period?.end);
                                break;
                            case 'RelatedPerson':
                                addRelatedPersonToList(participantEntry.resource.id ?? '',
                                    participantEntry.resource.name?.map(name => name.given?.join(',') + ' ' + name.family).find(() => true) ?? '',
                                    undefined,
                                    participant.period?.start,
                                    participant.period?.end)
                                break;
                            case 'Organization':
                                addOrganizationToList(participantEntry.resource.id ?? '',
                                    participantEntry.resource.name ?? '',
                                    undefined,
                                    participant.period?.start,
                                    participant.period?.end)
                                break;
                            case 'PractitionerRole':
                                addPractitionerRoleToList(participantEntry.resource.id ?? '',
                                    undefined,
                                    participant.period?.start,
                                    participant.period?.end);
                                break;
                            default:
                                break;
                        }
                    }
                });
            } else {
                setCreatedNow(true);
            }
        } catch (error) {
            onError();
        }
    }

    async function loadChoices(): Promise<void> {
        await Promise.all([
            loadPractitioners(),
            loadRelatedPerson(),
            loadOrganization(),
        ]);
    }

    const loadPractitioners = async (): Promise<void> => {
        try {
            const response = await fhirClient.search({
                resourceType: "Practitioner",
                searchParams: {
                    _elements: "id,name",
                },
            }) as Bundle;

            const result: Practitioner[] = [];

            response.entry?.forEach((entry) => {
                if (entry.resource?.resourceType === "Practitioner") {
                    result.push(entry.resource as Practitioner);
                }
            });

            setPractitionerChoices(result);
        } catch (error) {
            console.error("Erreur lors du chargement des praticiens :", error);
            setPractitionerChoices([]);
        }
    };

    const loadRelatedPerson = async (): Promise<void> => {
        try {
            const response = await fhirClient.search({
                resourceType: "RelatedPerson",
                searchParams: {
                    _elements: "id,name",
                },
            }) as Bundle;

            const result: RelatedPerson[] = [];

            response.entry?.forEach((entry) => {
                if (entry.resource?.resourceType === "RelatedPerson") {
                    result.push(entry.resource as RelatedPerson);
                }
            });

            setRelatedPersonChoices(result);
        } catch (error) {
            console.error("Erreur lors du chargement des personnes liées :", error);
            setRelatedPersonChoices([]);
        }
    };

    const loadOrganization = async (): Promise<void> => {
        try {
            const response = await fhirClient.search({
                resourceType: "Organization",
                searchParams: {
                    _elements: "id,name",
                },
            }) as Bundle;

            const result: Organization[] = [];

            response.entry?.forEach((entry) => {
                if (entry.resource?.resourceType === "Organization") {
                    result.push(entry.resource as Organization);
                }
            });

            setOrganizationChoices(result);
        } catch (error) {
            console.error("Erreur lors du chargement des organisations :", error);
            setOrganizationChoices([]);
        }
    };

    const pageLoadersRef = useRef({ loadPatient, loadCareTeam, loadChoices });
    pageLoadersRef.current = { loadPatient, loadCareTeam, loadChoices };

    useEffect(() => {
        const loadPage = async (): Promise<void> => {
            setLoading(true);

            try {
                await Promise.all([
                    pageLoadersRef.current.loadPatient(),
                    pageLoadersRef.current.loadCareTeam(),
                    pageLoadersRef.current.loadChoices(),
                ]);
            } finally {
                setLoading(false);
            }
        };

        void loadPage();
    }, []);

    function patientCivility(): string | undefined {
        let civility = "";
        if (gender === 'male') {
            civility = "Mr";
        } else if (gender === 'female') {
            civility = "Mme";
        } else {
            civility = "";
        }
        return civility;
    }

    function createCareTeam() {
        const newCareTeam = { "resourceType": "CareTeam" } as CareTeam;
        newCareTeam.id = "1";
        newCareTeam.status = "active";
        newCareTeam.name = "Cercle de soins de " + patientCivility() + " " + firstname + " " + lastname;
        newCareTeam.period = {
            "start": careTeamStartPeriod,
        }
        newCareTeam.subject = {
            "reference": "Patient/" + patientId
        }
        setCareTeam(newCareTeam);
    }

    function addAllPractitionerResources(entries: BundleEntry<FhirResource>[], start?: string, end?: string) {
        // replaceIds(entries);
        entries.forEach(entry => {
            const resourceType = entry.resource?.resourceType ?? '';
            switch (resourceType) {
                case "Practitioner":
                    const name = (entry.resource as Practitioner).name?.map(name => name.given?.join(",") + " " + name.family).find(() => true) ?? '';
                    participants.push({
                        id: entry.fullUrl ?? '',
                        type: 'Practitioner',
                        create: createParticipant,
                        value: entry.resource,
                        start: start ?? participantStart,
                        end: end ?? undefined
                    });
                    careCircleData.push({
                        id: entry.fullUrl ?? '',
                        participants: name,
                        startperiod: start ?? participantStart ?? 'N/A',
                        endperiod: end ?? "N/A",
                    });
                    break;
                case "PractitionerRole":
                    participants.push({
                        id: entry.fullUrl ?? '',
                        type: 'PractitionerRole',
                        create: createParticipant,
                        value: entry.resource,
                        start: start ?? participantStart,
                        end: end ?? undefined,
                        dependsOn: entries.map(e => e.fullUrl ?? '')
                    });
                    break;
                case "Organization":
                    console.log("Orga : " + JSON.stringify(entry.resource))
                    participants.push({
                        id: entry.fullUrl ?? '',
                        type: 'Organization',
                        create: createParticipant,
                        value: entry.resource,
                        start: start ?? participantStart,
                        end: end ?? undefined
                    })
                    careCircleData.push({
                        id: entry.fullUrl ?? '',
                        participants: (entry.resource as Organization).name ?? '',
                        startperiod: start ?? participantStart ?? 'N/A',
                        endperiod: end ?? "N/A",
                    });
                    break;
            }
        })
        setPractionnerToggle(false);
        setParticipantId('');
        setParticipantStart(undefined);
        setCreateParticipant(false);
    }

    function addPractitionerToList(id: string, name: string, data?: Practitioner, start?: string, end?: string) {
        participants.push({ id: id, type: 'Practitioner', create: createParticipant, value: data, start: start ?? participantStart, end: end ?? undefined })
        careCircleData.push({
            id: id,
            participants: name,
            startperiod: start ?? participantStart ?? 'N/A',
            endperiod: end ?? "N/A",
        });
        setPractionnerToggle(false);
        setParticipantId('');
        setParticipantStart(undefined);
        setCreateParticipant(false);
    }

    function addPractitionerRoleToList(id: string, data?: PractitionerRole, start?: string, end?: string) {
        participants.push({ id: id, type: 'PractitionerRole', create: createParticipant, value: data, start: start ?? participantStart, end: end ?? undefined })
    }

    function addRelatedPersonToList(id: string, name: string, data?: RelatedPerson, start?: string, end?: string) {
        participants.push({ id: id, type: 'RelatedPerson', create: createParticipant, value: data, start: start ?? participantStart, end: end ?? undefined })
        careCircleData.push({
            id: id,
            participants: name,
            startperiod: start ?? participantStart ?? 'N/A',
            endperiod: end ?? "N/A",
        });
        setRelatedPersonToggle(false);
        setParticipantId('');
        setParticipantStart(undefined);
        setCreateParticipant(false);
    }

    function addOrganizationToList(id: string, name: string, data?: Organization, start?: string, end?: string) {
        participants.push({ id: id, type: 'Organization', create: createParticipant, value: data, start: start ?? participantStart, end: end ?? undefined })
        careCircleData.push({
            id: id,
            participants: name,
            startperiod: start ?? participantStart ?? 'N/A',
            endperiod: end ?? "N/A",
        });
        setOrganizationToggle(false);
        setParticipantId('');
        setParticipantStart(undefined);
        setCreateParticipant(false);
    }

    async function saveCareCircle() {
        try {
            const created = [] as { oldId: string, newId: string, ref: string, start: string | undefined, end: string | undefined, type: string }[];
            for (const participant of participants) {
                if (participant.create) {
                    switch (participant.type) {
                        case "Practitioner":
                            const practitioner = await fhirClient.create({
                                resourceType: 'Practitioner',
                                body: participant.value as Practitioner,
                            });
                            created.push({ oldId: participant.id, newId: practitioner.id, ref: "Practitioner/" + practitioner.id, start: participant.start, end: participant.end, type: 'Practitioner' });
                            break;
                        case "PractitionerRole": {
                            let practitionerRoleResource = JSON.stringify(participant.value);
                            created.forEach(toReplace => {
                                if (participant.dependsOn?.find(d => d === toReplace.oldId) !== undefined) {
                                    practitionerRoleResource = practitionerRoleResource.replaceAll(toReplace.oldId, toReplace.type + '/' + toReplace.newId);
                                }
                            });

                            const practitionerRole = await fhirClient.create({
                                resourceType: 'PractitionerRole',
                                body: JSON.parse(practitionerRoleResource) as unknown as PractitionerRole,
                            });
                            created.push({ oldId: participant.id, newId: practitionerRole.id, ref: "PractitionerRole/" + practitionerRole.id, start: participant.start, end: participant.end, type: 'PractitionerRole' });
                            break;
                        }
                        case "RelatedPerson": {
                            let relatedPersonResource = JSON.stringify(participant.value);
                            created.forEach(toReplace => {
                                relatedPersonResource = relatedPersonResource.replaceAll(toReplace.oldId, toReplace.newId)
                            });

                            const relatedPerson = await fhirClient.create({
                                resourceType: "RelatedPerson",
                                body: JSON.parse(relatedPersonResource) as unknown as RelatedPerson
                            });
                            created.push({ oldId: participant.id, newId: relatedPerson.id, ref: "RelatedPerson/" + relatedPerson.id, start: participant.start, end: participant.end, type: 'RelatedPerson' });
                            break;
                        }
                        case "Organization": {
                            let organizationResource = JSON.stringify(participant.value);
                            created.forEach(toReplace => {
                                organizationResource = organizationResource.replaceAll(toReplace.oldId, toReplace.newId)
                            });

                            const organization = await fhirClient.create({
                                resourceType: "Organization",
                                body: JSON.parse(organizationResource) as unknown as Organization
                            });
                            created.push({ oldId: participant.id, newId: organization.id, ref: "Organization/" + organization.id, start: participant.start, end: participant.end, type: 'Organization' });
                            break;
                        }
                    }
                }
            }

            if (careTeam) {
                careTeam.participant = [];

                participants.filter(p => !p.create).forEach(p => {
                    careTeam?.participant?.push({
                        "member": {
                            "reference": p.type + "/" + p.id
                        },
                        "period": {
                            "start": p.start,
                            "end": p.end
                        }
                    }
                    )
                });

                created.forEach(c => {
                    careTeam?.participant?.push({
                        "member": {
                            "reference": c.ref
                        },
                        "period": {
                            "start": c.start,
                            "end": c.end
                        }
                    }
                    )
                });

                if (createdNow) {
                    await fhirClient.create({
                        resourceType: "CareTeam",
                        body: careTeam ?? { resourceType: 'CareTeam' }
                    });
                } else {
                    await fhirClient.update({
                        resourceType: "CareTeam",
                        id: careTeam?.id ?? '',
                        body: careTeam ?? { resourceType: 'CareTeam' }
                    });
                    setCreatedNow(false);
                }
            }
            reloadPage();
        } catch (error) {
            console.log(error);
            onError();
        }
    }

    /**
    * Capitalize the first letter 
    * 
    * @param string the string to capitalize
    */
    function capitalizeFirst(string: string): string {
        return string.charAt(0).toUpperCase() + string.slice(1);
    }

    /////////////////////////////////////
    //             Content               //
    //////////////////////////////////

    return (
        <AcesoPage loading={loading} needsLogin={true} >
            <>
                <div className={styles.header}>
                    <div className="flexWrapStart">
                        <img
                            className={styles.image}
                            src={toPublicUrl("/assets/temporaryPatientPhoto.png")}
                            alt={t("forms.careCircle.patientPortrait")} />
                        <div>
                            <div className={[styles.whiteText, styles.text].join(' ')}>
                                <Title level={2} content={firstname + " " + lastname} />
                            </div>
                            <p className={[styles.whiteText, styles.text].join(' ')}>
                                {capitalizeFirst(gender)}
                            </p>
                            <p className={[styles.whiteText, styles.text].join(' ')}>
                                {birthdate}
                            </p>
                        </div>
                    </div>
                </div>
                <div className={styles.tabsSection}>
                    <NavTabs
                        tabs={[
                            {
                                title: i18n.t('title.carecircle'),
                                content: <div className="section">
                                    {careTeam !== undefined ?
                                        <>
                                            <div>
                                                <Title level={3} content={careTeam?.name ?? ''} />
                                                <div>
                                                    <Form.Label className={styles.formLabel}>
                                                        <strong>
                                                            {i18n.t('label.startperiod')} :
                                                        </strong>
                                                    </Form.Label>
                                                    <Form.Text>
                                                        {careTeamStartPeriod}
                                                    </Form.Text>
                                                </div>
                                                <div>
                                                    <Form.Label className={styles.formLabel}>
                                                        <strong>
                                                            {i18n.t('label.endperiod')} :
                                                        </strong>
                                                    </Form.Label>
                                                    <Form.Text>
                                                        {careTeamEndPeriod}
                                                    </Form.Text>
                                                </div>
                                            </div>
                                            <br />
                                            <div className="mediumContainer">
                                                <Dropdown>
                                                    <Dropdown.Toggle variant="primary">
                                                        {t("forms.careCircle.addParticipant")}
                                                    </Dropdown.Toggle>
                                                    <Dropdown.Menu>
                                                        <Dropdown.Item
                                                            onClick={() => {
                                                                setPractionnerToggle(true);
                                                                setRelatedPersonToggle(false);
                                                                setOrganizationToggle(false);
                                                                forceUpdate();
                                                            }
                                                            }
                                                        >
                                                            {t("forms.note.authorTypes.Practitioner")}
                                                        </Dropdown.Item>

                                                        <Dropdown.Item
                                                            onClick={() => {
                                                                setPractionnerToggle(false);
                                                                setRelatedPersonToggle(true);
                                                                setOrganizationToggle(false);
                                                                forceUpdate();
                                                            }
                                                            }
                                                        >
                                                            {t("forms.note.authorTypes.RelatedPerson")}
                                                        </Dropdown.Item>

                                                        <Dropdown.Item
                                                            onClick={() => {
                                                                setPractionnerToggle(false);
                                                                setRelatedPersonToggle(false);
                                                                setOrganizationToggle(true);
                                                                forceUpdate();
                                                            }
                                                            }
                                                        >
                                                            {t("forms.note.authorTypes.Organization")}
                                                        </Dropdown.Item>
                                                    </Dropdown.Menu>
                                                </Dropdown>
                                            </div>
                                            {practionnerToggle &&
                                                <div className="mediumContainer">
                                                    <Card>
                                                        <Card.Body>
                                                            <Form.Group className="col">
                                                                <Form.Label>
                                                                    {t("forms.careCircle.addParticipantType", { type: t("forms.note.authorTypes.Practitioner") })}
                                                                </Form.Label>
                                                                <Form.Check
                                                                    type="switch"
                                                                    label={t("forms.careCircle.createParticipant", { type: t("forms.note.authorTypes.Practitioner") })}
                                                                    checked={createParticipant}
                                                                    onChange={() => {
                                                                        setCreateParticipant(!createParticipant);
                                                                    }}
                                                                />

                                                                {createParticipant ? (
                                                                    <>
                                                                        <Form.Group className={styles.subForm}>
                                                                            <PractitionerForm
                                                                                submitTrigger={participantSubmitTrigger}
                                                                                resetTrigger={participantResetTrigger}
                                                                                dataSubmit={pro => {
                                                                                    addAllPractitionerResources(pro, participantStart);
                                                                                    triggerReset(true);
                                                                                    triggerSubmit(false);
                                                                                }} />
                                                                        </Form.Group>
                                                                    </>
                                                                ) :
                                                                    <Form.Select
                                                                        className="select"
                                                                        defaultValue={undefined}
                                                                        onChange={(event) => {
                                                                            setParticipantId(event.target.value);
                                                                        }}
                                                                    >
                                                                        <option
                                                                            value={undefined}
                                                                        >
                                                                            {t("forms.careCircle.selectParticipant", { type: t("forms.note.authorTypes.Practitioner") })}
                                                                        </option>
                                                                        {practitionerChoices.map((practitioner) => {
                                                                            return <option
                                                                                value={practitioner.id + "&&" + practitioner.name?.map(name => name.given?.join(",") + " " + name.family)}
                                                                            >
                                                                                {practitioner.name?.map(name => name.given?.join(",") + " " + name.family)}
                                                                            </option>
                                                                        })}
                                                                    </Form.Select>
                                                                }
                                                            </Form.Group>
                                                            <Form.Group>
                                                                <Form.Label>
                                                                    {i18n.t('label.startperiod')} :
                                                                </Form.Label>
                                                                <Form.Control
                                                                    type="date"
                                                                    value={participantStart}
                                                                    onChange={(event) => {
                                                                        setParticipantStart(event.currentTarget.value);
                                                                    }
                                                                    }
                                                                />
                                                            </Form.Group>
                                                            <Button
                                                                className="button"
                                                                variant="danger"
                                                                onClick={() => {
                                                                    if (createParticipant) {
                                                                        triggerSubmit(true);
                                                                    } else {
                                                                        const ref = participantId.split("&&");
                                                                        addPractitionerToList(ref[0], ref[1]);
                                                                    }
                                                                }
                                                                }
                                                            >
                                                                {i18n.t('button.add')}
                                                            </Button>
                                                        </Card.Body>
                                                    </Card>
                                                </div>
                                            }
                                            {relatedPersonToggle &&
                                                <div className="mediumContainer">
                                                    <Card>
                                                        <Card.Body>
                                                            <Form.Group className="col">
                                                                <Form.Label>
                                                                    {t("forms.careCircle.addParticipantType", { type: t("forms.note.authorTypes.RelatedPerson") })}
                                                                </Form.Label>
                                                                <Form.Check
                                                                    type="switch"
                                                                    label={t("forms.careCircle.createParticipant", { type: t("forms.note.authorTypes.RelatedPerson") })}
                                                                    checked={createParticipant}
                                                                    onChange={() => {
                                                                        setCreateParticipant(!createParticipant);
                                                                    }}
                                                                />

                                                                {createParticipant ? (
                                                                    <>
                                                                        <Form.Group className={styles.subForm}>
                                                                            <RelatedPersonForm
                                                                                submitTrigger={participantSubmitTrigger}
                                                                                resetTrigger={participantResetTrigger}
                                                                                relatedPersonSubmit={(rp) => {
                                                                                    const name = rp.name?.map(name => name.given?.join(",") + " " + name.family).find(() => true) ?? '';
                                                                                    addRelatedPersonToList(String(counter), name, rp);
                                                                                    triggerReset(true);
                                                                                    triggerSubmit(false);
                                                                                }} />
                                                                        </Form.Group>
                                                                    </>
                                                                ) :
                                                                    <Form.Select
                                                                        className="select"
                                                                        defaultValue={undefined}
                                                                        onChange={(event) => {
                                                                            setParticipantId(event.target.value);
                                                                        }}
                                                                    >
                                                                        <option
                                                                            value={undefined}
                                                                        >
                                                                            {t("forms.careCircle.selectParticipant", { type: t("forms.note.authorTypes.RelatedPerson") })}
                                                                        </option>
                                                                        {relatedPersonChoices.map((relatedPerson) => {
                                                                            return <option
                                                                                value={relatedPerson.id + "&&" + relatedPerson.name?.map(name => name.given?.join(",") + " " + name.family)}
                                                                            >
                                                                                {relatedPerson.name?.map(name => name.given?.join(",") + " " + name.family)}
                                                                            </option>
                                                                        })}
                                                                    </Form.Select>
                                                                }
                                                            </Form.Group>
                                                            <Form.Group>
                                                                <Form.Label>
                                                                    {i18n.t('label.startperiod')} :
                                                                </Form.Label>
                                                                <Form.Control
                                                                    type="date"
                                                                    value={participantStart}
                                                                    onChange={(event) => {
                                                                        setParticipantStart(event.currentTarget.value);
                                                                    }
                                                                    }
                                                                />
                                                            </Form.Group>
                                                            <Button
                                                                className="button"
                                                                variant="danger"
                                                                onClick={() => {
                                                                    if (createParticipant) {
                                                                        triggerSubmit(true);
                                                                    } else {
                                                                        const ref = participantId.split("&&");
                                                                        addRelatedPersonToList(ref[0], ref[1]);
                                                                    }
                                                                }
                                                                }
                                                            >
                                                                {i18n.t('button.add')}
                                                            </Button>
                                                        </Card.Body>
                                                    </Card>
                                                </div>
                                            }
                                            {organizationToggle &&
                                                <div className="mediumContainer">
                                                    <Card>
                                                        <Card.Body>
                                                            <Form.Group className="col">
                                                                <Form.Label>
                                                                    {t("forms.careCircle.addParticipantType", { type: t("forms.note.authorTypes.Organization") })}
                                                                </Form.Label>
                                                                <Form.Check
                                                                    type="switch"
                                                                    label={t("forms.careCircle.createParticipant", { type: t("forms.note.authorTypes.Organization") })}
                                                                    checked={createParticipant}
                                                                    onChange={() => {
                                                                        setCreateParticipant(!createParticipant);
                                                                    }}
                                                                />

                                                                {createParticipant ? (
                                                                    <>
                                                                        <Form.Group className={styles.subForm}>
                                                                            <OrganizationForm
                                                                                submitTrigger={participantSubmitTrigger}
                                                                                resetTrigger={participantResetTrigger}
                                                                                organizationSubmit={(o) => {
                                                                                    addOrganizationToList(String(counter), o.name ?? '', o);
                                                                                    triggerReset(true);
                                                                                    triggerSubmit(false);
                                                                                }} />
                                                                        </Form.Group>
                                                                    </>
                                                                ) :
                                                                    <Form.Select
                                                                        className="select"
                                                                        defaultValue={undefined}
                                                                        onChange={(event) => {
                                                                            setParticipantId(event.target.value);
                                                                        }}
                                                                    >
                                                                        <option
                                                                            value={undefined}
                                                                        >
                                                                            {t("forms.careCircle.selectParticipant", { type: t("forms.note.authorTypes.Organization") })}
                                                                        </option>
                                                                        {organizationChoices.map((organization) => {
                                                                            return <option
                                                                                value={organization.id + "&&" + organization.name}
                                                                            >
                                                                                {organization.name}
                                                                            </option>
                                                                        })}
                                                                    </Form.Select>
                                                                }
                                                            </Form.Group>
                                                            <Form.Group>
                                                                <Form.Label>
                                                                    {i18n.t('label.startperiod')} :
                                                                </Form.Label>
                                                                <Form.Control
                                                                    type="date"
                                                                    value={participantStart}
                                                                    onChange={(event) => {
                                                                        setParticipantStart(event.currentTarget.value);
                                                                    }
                                                                    }
                                                                />
                                                            </Form.Group>
                                                            <Button
                                                                className="button"
                                                                variant="danger"
                                                                onClick={() => {
                                                                    if (createParticipant) {
                                                                        triggerSubmit(true);
                                                                    } else {
                                                                        const ref = participantId.split("&&");
                                                                        addOrganizationToList(ref[0], ref[1]);
                                                                    }
                                                                }
                                                                }
                                                            >
                                                                {i18n.t('button.add')}
                                                            </Button>
                                                        </Card.Body>
                                                    </Card>
                                                </div>
                                            }
                                            <div className="section">
                                                <DataTable
                                                    keyField="id"
                                                    data={careCircleData}
                                                    columns={careCirclecolumns}
                                                />
                                            </div>
                                            <br />
                                            <Button
                                                className="button"
                                                variant="danger"
                                                onClick={() => {
                                                    saveCareCircle();
                                                }
                                                }
                                            >
                                                {i18n.t('button.save')}
                                            </Button>
                                        </>
                                        :
                                        <>
                                            <Alert variant="info">
                                                {i18n.t('general.carecirclecreationalert')} !
                                            </Alert>
                                            <br />
                                            <Card>
                                                <Card.Body>
                                                    <Form.Label>
                                                        {i18n.t('label.startperiod')} :
                                                    </Form.Label>
                                                    <div className="mediumContainer">
                                                        <InputGroup className="mb-3">
                                                            <Form.Control
                                                                type="date"
                                                                value={careTeamStartPeriod}
                                                                onChange={(event) => {
                                                                    setCareTeamStartPeriod(event.currentTarget.value);
                                                                }
                                                                }
                                                            />
                                                        </InputGroup>
                                                    </div>
                                                    <Button
                                                        className={styles.largeButton}
                                                        variant="danger"
                                                        onClick={createCareTeam}
                                                    >
                                                        {i18n.t('button.createcarecircle')}
                                                    </Button>
                                                </Card.Body>
                                            </Card>
                                        </>
                                    }
                                </div>
                            },
                            {
                                title: i18n.t('title.communicationlog'),
                                content: <NoteList baseParameters={{ "subject": patientId }}></NoteList>
                            }
                        ]}
                    />
                </div>
            </>
        </AcesoPage >
    );
};

export default PatientsViewer;
